import { FC, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Stack } from '@mui/material';

import { AnalyticsLoadingBox } from 'components/AnalyticsBox/AnalyticsLoadingBox';
import { MonthFilter } from 'components/FilterComponents/MonthFilter/MonthFilter';
import { getPreviousCalendarMonth } from 'components/FilterComponents/MonthFilter/MonthFilter.utils';

import {
  buildPlaceholderApiMetrics,
  formatMonthForBackend
} from './AnalyticsApiPerformanceTab.utils';
import { ApiPerformanceCard } from './components/ApiPerformanceCard';
import { useApiPerformanceMetrics } from './hooks/useApiPerformanceMetrics';

const AnalyticsApiPerformanceTab: FC = () => {
  const { publisherId } = useParams();
  const maxSelectableMonth = useMemo(() => getPreviousCalendarMonth(), []);
  const [selectedMonth, setSelectedMonth] = useState<Date>(maxSelectableMonth);

  const month = useMemo(
    () => formatMonthForBackend(selectedMonth),
    [selectedMonth]
  );

  const { data: apiPerformanceData, isLoading } = useApiPerformanceMetrics({
    publisherId,
    month
  });

  const hasApiMetrics = (apiPerformanceData?.apiMetrics?.length ?? 0) > 0;
  const shouldRenderLoadingPlaceholder = isLoading && !hasApiMetrics;

  const apiMetricsToRender = useMemo(() => {
    const responseApiMetrics = apiPerformanceData?.apiMetrics ?? [];
    if (responseApiMetrics.length > 0) {
      return responseApiMetrics;
    }
    return buildPlaceholderApiMetrics();
  }, [apiPerformanceData]);

  const apiPerformanceContent = useMemo(() => {
    if (shouldRenderLoadingPlaceholder) {
      return <AnalyticsLoadingBox />;
    }
    return apiMetricsToRender.map((apiMetric) => (
      <ApiPerformanceCard
        key={apiMetric.apiName}
        apiMetric={apiMetric}
        isLoading={isLoading}
      />
    ));
  }, [shouldRenderLoadingPlaceholder, apiMetricsToRender, isLoading]);

  return (
    <Stack direction="column" className="pb-[57px]">
      <div className="w-full mt-1 mb-5 flex justify-start items-center">
        <MonthFilter
          dataTestId="api-performance-month-filter"
          selectedMonth={selectedMonth}
          onSelectedMonthChange={setSelectedMonth}
          maxSelectableMonth={maxSelectableMonth}
        />
      </div>

      <div className="grid grid-cols-1 gap-4">{apiPerformanceContent}</div>
    </Stack>
  );
};

export default AnalyticsApiPerformanceTab;
